import React from 'react';
import { useFontSize } from '../../contexts/FontSizeContext';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { getTextSizeClass, getHeaderTextSizeClass } from '../../utils/fontSizeUtils';
import { HISTORICAL_PRICE_STATISTICS } from '../../utils/dailyPriceUtils';
import { formatDailyPriceStatisticValue } from '../../utils/dailyPriceUtils';
import { TickerDailyData } from './DailyPriceTable';

interface DailyPriceTableFullScreenProps {
  data: TickerDailyData[];
  loading: boolean;
  loadingMore: boolean;
  sortColumn: 'date' | 'close_price_adjusted' | 'num_transactions' | 'volume_adjusted';
  sortDirection: 'asc' | 'desc';
  onSort: (column: 'date' | 'close_price_adjusted' | 'num_transactions' | 'volume_adjusted') => void;
  onClose: () => void;
  tableRef?: React.RefObject<HTMLDivElement>;
  isSyncing?: boolean;
}

export const DailyPriceTableFullScreen: React.FC<DailyPriceTableFullScreenProps> = ({
  data,
  loading,
  loadingMore,
  sortColumn,
  sortDirection,
  onSort,
  onClose,
  tableRef,
  isSyncing = false,
}) => {
  const { fontSize } = useFontSize();
  useBodyScrollLock(true);

  const renderSortIcon = (statisticKey: string) => {
    if (sortColumn === statisticKey) {
      return sortDirection === 'desc' ? (
        <svg
          className="w-4 h-4 text-highlight-gold"
          fill="currentColor"
          viewBox="0 0 20 20"
          style={{ minWidth: '16px' }}
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg
          className="w-4 h-4 text-highlight-gold"
          fill="currentColor"
          viewBox="0 0 20 20"
          style={{ minWidth: '16px' }}
        >
          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
        </svg>
      );
    } else {
      return (
        <svg
          className="w-4 h-4 text-gray-400"
          fill="currentColor"
          viewBox="0 0 20 20"
          style={{ minWidth: '16px' }}
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    }
  };

  const cellClassName = (statisticKey: string) => {
    return `${statisticKey === 'date' ? 'pl-2 pr-3 sm:pl-3 sm:pr-4 md:pr-3 lg:pl-5 lg:pr-4' : 'px-1 sm:px-2 md:px-4 lg:px-6'} whitespace-nowrap ${getTextSizeClass(fontSize)} font-semibold h-8 sm:h-10 bg-surface-light dark:bg-background-card ${statisticKey === 'date' ? 'transition-none' : ''}`;
  };

  const headerCellClassName = (statisticKey: string) => {
    return `py-1 ${statisticKey === 'date' ? 'pl-2 pr-3 sm:pl-3 sm:pr-4 md:pr-3 lg:pl-5 lg:pr-4' : 'px-1 sm:px-2 md:px-4 lg:px-6'} text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-8 sm:h-10 bg-gray-50 dark:bg-background-card cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-none`;
  };

  return (
    <>
    <style dangerouslySetInnerHTML={{
      __html: `
        /* Sticky header top border - using a class-based approach */
        table thead.sticky-header-border::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead.sticky-header-border::before {
          background-color: rgb(55, 65, 81);
        }
        
        /* Sticky header bottom border - using a class-based approach */
        table thead.sticky-header-border::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead.sticky-header-border::after {
          background-color: rgb(55, 65, 81);
        }
      `
    }} />
    <div
      className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={tableRef}
        className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain"
      >
          <table className="min-w-full font-mono" style={{ tableLayout: 'auto', width: '100%' }}>
            <colgroup>
              <col />
              <col />
              <col />
              <col />
            </colgroup>
            
            {/* Header with sticky positioning */}
            <thead className="bg-gray-50 dark:bg-background-card sticky-header-border" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
              <tr>
                {/* Statistics headers */}
                {HISTORICAL_PRICE_STATISTICS.map((statistic) => (
                  <th
                    key={statistic.key}
                    className={headerCellClassName(statistic.key)}
                    style={{
                      ...(statistic.key === 'date' ? { position: 'sticky', left: 0, zIndex: 40 } : {})
                    }}
                    onClick={() => onSort(statistic.key as typeof sortColumn)}
                  >
                    <div className="flex items-center">
                      <span>{statistic.label}</span>
                      <div className="ml-1 w-5 h-4 flex items-center justify-center">
                        {renderSortIcon(statistic.key)}
                      </div>
                    </div>
                  </th>
                ))}
                
                {/* Close button - sticky right */}
                <th
                  className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                  style={{ position: 'sticky', right: 0, zIndex: 40 }}
                >
                  <button
                    onClick={onClose}
                    className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                    title="Close full screen"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </th>
              </tr>
            </thead>
            
            {/* Table body */}
            <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
              {(isSyncing || (loading && data.length === 0)) ? (
                // Show placeholder rows with loading animation
                Array.from({ length: 30 }).map((_, index) => (
                  <tr key={`loading-${index}`}>
                    {HISTORICAL_PRICE_STATISTICS.map((statistic) => (
                      <td
                        key={statistic.key}
                        className={cellClassName(statistic.key)}
                        style={{
                          ...(statistic.key === 'date' ? { position: 'sticky', left: 0, zIndex: 30 } : {})
                        }}
                      >
                        <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 rounded" style={{ width: statistic.key === 'date' ? '80px' : '60px' }}></div>
                      </td>
                    ))}
                    {/* Empty cell for close button column */}
                    <td
                      className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-surface-light dark:bg-background-card`}
                      style={{ position: 'sticky', right: 0, zIndex: 30 }}
                    ></td>
                  </tr>
                ))
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={HISTORICAL_PRICE_STATISTICS.length + 1} className="px-4 py-8 text-center text-gray-600 dark:text-text-secondary">
                    No data available
                  </td>
                </tr>
              ) : (
                data.map((rowData, index) => (
                  <tr key={`${rowData.date}-${index}`}>
                    {HISTORICAL_PRICE_STATISTICS.map((statistic) => (
                      <td
                        key={statistic.key}
                        className={`${cellClassName(statistic.key)} text-text-light-neutral dark:text-text-secondary`}
                        style={{
                          ...(statistic.key === 'date' ? { position: 'sticky', left: 0, zIndex: 30 } : {})
                        }}
                      >
                        {formatDailyPriceStatisticValue(statistic.key, rowData, false)}
                      </td>
                    ))}
                    
                    {/* Empty cell for close button column */}
                    <td
                      className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-surface-light dark:bg-background-card`}
                      style={{ position: 'sticky', right: 0, zIndex: 30 }}
                    ></td>
                  </tr>
                ))
              )}
              {loadingMore && (
                <tr>
                  <td colSpan={HISTORICAL_PRICE_STATISTICS.length + 1} className="px-1 sm:px-2 md:px-4 lg:px-6 py-2 sm:py-4 text-center text-gray-500 dark:text-text-secondary">
                    Loading more data...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
      </div>
    </div>
    </>
  );
};

